import type { ProjectSummary } from '../../persist/api';
import type { ProjectStoreState } from '../../state/api';
import type { RuntimeState, StudioApi } from '../../engine/api';
import type { Actions } from '../actions';
import { tidyMenu, type MenuEntry } from '../components/menuModel';
import { transformMenu } from '../panels/sources/itemMenu';
import type { UiStore } from '../uiStore';
import { BUILTIN_PANELS, PLUGIN_PANEL_PREFIX } from './layoutModel';

export interface MenuContext {
  studio: StudioApi;
  ui: UiStore;
  actions: Actions;
  collections: readonly ProjectSummary[];
  /** Display text of the first chord bound to an action. */
  shortcut(actionId: string): string | undefined;
}

export const MENU_TITLES = ['File', 'Edit', 'View', 'Scene Collection', 'Help'] as const;
export type MenuTitle = (typeof MENU_TITLES)[number];

function docksMenu({ ui, studio }: MenuContext): MenuEntry[] {
  const dock = ui.getState().dock;
  const runtime = studio.runtime.getState();
  const panels = [...BUILTIN_PANELS, ...runtime.docks.map((d) => ({ id: PLUGIN_PANEL_PREFIX + d.id, title: d.title }))];
  return [
    ...panels.map((panel) => ({
      label: panel.title,
      checked: dock?.isOpen(panel.id) ?? false,
      disabled: !dock,
      onSelect: () => dock?.toggle(panel.id),
    })),
    { separator: true },
    { label: 'Reset layout', disabled: !dock, onSelect: () => dock?.reset() },
  ];
}

function collectionEntries({ studio, actions, collections }: MenuContext): MenuEntry[] {
  const currentId = studio.runtime.getState().collection.id;
  return collections.map((entry) => ({
    label: entry.name,
    checked: entry.id === currentId,
    onSelect: () => {
      if (entry.id !== currentId) actions.openCollection(entry.id);
    },
  }));
}

export function buildMenu(title: MenuTitle, ctx: MenuContext): MenuEntry[] {
  const { studio, ui, actions, shortcut } = ctx;
  const open = ui.getState().openDialog;
  const history: ProjectStoreState = studio.project.getState();
  const runtime: RuntimeState = studio.runtime.getState();
  const hasSelection = actions.selectedIds().length > 0;
  const clipboard = ui.getState().clipboard;

  switch (title) {
    case 'File':
      return tidyMenu([
        { label: 'New scene collection…', onSelect: () => actions.newCollection() },
        { label: 'Open scene collection…', onSelect: () => open({ type: 'collections' }) },
        { label: 'Duplicate scene collection…', onSelect: () => actions.duplicateCollection() },
        { label: 'Rename scene collection…', onSelect: () => actions.renameCollection() },
        {
          label: 'Delete scene collection…',
          danger: true,
          submenu: ctx.collections
            .filter((c) => c.id !== runtime.collection.id)
            .map((c) => ({ label: c.name, danger: true, onSelect: () => actions.deleteCollection(c.id, c.name) })),
          disabled: ctx.collections.filter((c) => c.id !== runtime.collection.id).length === 0,
        },
        { separator: true },
        { label: 'Import scene collection…', onSelect: () => actions.importCollection() },
        { label: 'Export scene collection', onSelect: () => actions.exportCollection(false) },
        { label: 'Export with media files', onSelect: () => actions.exportCollection(true) },
        { separator: true },
        { label: 'Save now', shortcut: shortcut('file.save'), onSelect: () => actions.save() },
        { label: 'Show recordings…', onSelect: () => open({ type: 'recordings' }) },
        { separator: true },
        { label: 'Settings…', onSelect: () => open({ type: 'settings' }) },
      ]);

    case 'Edit':
      return tidyMenu([
        {
          label: history.undoLabel ? `Undo ${history.undoLabel}` : 'Undo',
          shortcut: shortcut('edit.undo'),
          disabled: !history.canUndo,
          onSelect: () => studio.project.undo(),
        },
        {
          label: history.redoLabel ? `Redo ${history.redoLabel}` : 'Redo',
          shortcut: shortcut('edit.redo'),
          disabled: !history.canRedo,
          onSelect: () => studio.project.redo(),
        },
        { separator: true },
        { label: 'Copy', shortcut: shortcut('edit.copy'), disabled: !hasSelection, onSelect: () => actions.copyItems() },
        { label: 'Paste', shortcut: shortcut('edit.paste'), disabled: !clipboard.items, onSelect: () => actions.pasteItems() },
        { label: 'Duplicate', shortcut: shortcut('edit.duplicate'), disabled: !hasSelection, onSelect: () => actions.duplicate('reference') },
        { label: 'Remove', shortcut: shortcut('edit.delete'), disabled: !hasSelection, danger: true, onSelect: () => actions.removeItems() },
        { separator: true },
        { label: 'Select all', shortcut: shortcut('edit.select-all'), onSelect: () => actions.selectAll() },
        { separator: true },
        { label: 'Transform', disabled: !hasSelection, submenu: transformMenu(actions) },
        { label: 'Copy transform', disabled: !hasSelection, onSelect: () => actions.copyTransform() },
        { label: 'Paste transform', disabled: !hasSelection || !clipboard.transform, onSelect: () => actions.pasteTransform() },
        { separator: true },
        { label: 'Advanced audio properties…', onSelect: () => open({ type: 'advanced-audio' }) },
      ]);

    case 'View':
      return tidyMenu([
        { label: 'Docks', submenu: docksMenu(ctx) },
        { separator: true },
        {
          label: 'Studio mode',
          checked: runtime.studioMode,
          shortcut: shortcut('studio.toggle-mode'),
          onSelect: () => actions.toggleStudioMode(),
        },
        {
          label: 'Stats',
          checked: ui.getState().dock?.isOpen('stats') ?? false,
          disabled: !ui.getState().dock,
          onSelect: () => ui.getState().dock?.toggle('stats'),
        },
        { separator: true },
        { label: 'Reset layout', disabled: !ui.getState().dock, onSelect: () => ui.getState().dock?.reset() },
      ]);

    case 'Scene Collection':
      return tidyMenu([
        ...collectionEntries(ctx),
        { separator: true },
        { label: 'New…', onSelect: () => actions.newCollection() },
        { label: 'Manage…', onSelect: () => open({ type: 'collections' }) },
      ]);

    case 'Help':
      return [
        { label: 'Keyboard and mouse shortcuts', onSelect: () => open({ type: 'shortcuts' }) },
        { label: 'Browser limitations', onSelect: () => open({ type: 'settings', tab: 'about' }) },
        { label: 'Streaming tools', onSelect: () => open({ type: 'streaming-tools' }) },
        { separator: true },
        { label: 'About', onSelect: () => open({ type: 'about' }) },
      ];
  }
}
